<div class="space-y-6">
  <app-page-header
    title="Scopes"
    subtitle="Actions disponibles sur les resources (create, read, update, delete, …)."
  >
    <button
      slot="actions"
      type="button"
      (click)="openCreate()"
      class="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-5 py-2.5 text-sm font-medium text-white shadow-theme-xs transition hover:bg-brand-600"
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M10 4.167v11.666M4.167 10h11.666" stroke="currentColor" stroke-width="1.667" stroke-linecap="round" />
      </svg>
      Nouveau scope
    </button>
  </app-page-header>

  <app-permissions-tabs />

  <section class="rounded-2xl border border-gray-200 bg-white shadow-theme-xs dark:border-gray-800 dark:bg-white/[0.03]">
    @if (error() || loading() || scopes().length === 0) {
      <app-data-state
        [loading]="loading()"
        [error]="error()"
        [empty]="!loading() && !error() && scopes().length === 0"
        emptyMessage="Aucun scope."
        (retry)="load()"
      />
    } @else {
      <div class="overflow-x-auto rounded-2xl">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead class="bg-gray-50 dark:bg-white/[0.02]">
            <tr>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Nom</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Libellé</th>
              <th class="px-5 py-3 text-left text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">ID</th>
              <th class="px-5 py-3 text-right text-theme-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
            @for (s of scopes(); track s.id || s.name) {
              <tr class="transition hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                <td class="px-5 py-4"><span class="font-mono text-sm font-medium text-gray-800 dark:text-white/90">{{ s.name }}</span></td>
                <td class="px-5 py-4 text-sm text-gray-700 dark:text-gray-300">{{ s.displayName || '—' }}</td>
                <td class="px-5 py-4 font-mono text-[11px] text-gray-500 dark:text-gray-400 break-all">{{ s.id || '—' }}</td>
                <td class="whitespace-nowrap px-5 py-4 text-right">
                  <button
                    type="button"
                    (click)="openDelete(s)"
                    class="text-xs font-medium text-error-600 hover:text-error-700 dark:text-error-400"
                  >
                    Supprimer
                  </button>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="border-t border-gray-200 px-5 py-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {{ scopes().length }} scope(s)
      </div>
    }
  </section>
</div>

<!-- Create modal -->
<app-modal [isOpen]="createOpen()" (close)="closeCreate()" className="w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-dark">
  <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Nouveau scope</h3>
  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
    Action accessible sur une ou plusieurs resources. Ex. <code class="font-mono">create</code>, <code class="font-mono">user:read</code>.
  </p>

  <form [formGroup]="createForm" (ngSubmit)="submitCreate()" class="mt-4 space-y-4">
    <div>
      <label for="scope-name" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">
        Nom <span class="text-error-500">*</span>
      </label>
      <input
        id="scope-name"
        type="text"
        formControlName="name"
        placeholder="ex. create, read, vehicle:update"
        class="h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 font-mono text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
        [class.border-error-500]="showCreateError('name')"
        [class.border-gray-300]="!showCreateError('name')"
        [class.dark:border-gray-700]="!showCreateError('name')"
      />
      @if (showCreateError('name')) {
        <p class="mt-1.5 text-xs text-error-500">Champ obligatoire (lettres, chiffres, . _ - : uniquement).</p>
      }
    </div>
    <div>
      <label for="scope-display" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Libellé</label>
      <input
        id="scope-display"
        type="text"
        formControlName="displayName"
        placeholder="Libellé affiché"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
      />
    </div>
    <div>
      <label for="scope-icon" class="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-400">Icon URI</label>
      <input
        id="scope-icon"
        type="text"
        formControlName="iconUri"
        placeholder="URL d'icône (optionnel)"
        class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
      />
    </div>

    @if (createError(); as msg) {
      <div class="rounded-lg border border-error-200 bg-error-50 px-3 py-2 text-xs text-error-700 dark:border-error-800 dark:bg-error-500/10 dark:text-error-400">{{ msg }}</div>
    }

    <div class="flex justify-end gap-2 pt-2">
      <button type="button" (click)="closeCreate()" [disabled]="creating()" class="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5">Annuler</button>
      <button type="submit" [disabled]="creating() || createForm.invalid" class="inline-flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-brand-300">
        @if (creating()) {
          <svg class="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-opacity="0.25" />
            <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" />
          </svg>
          Création…
        } @else { Créer le scope }
      </button>
    </div>
  </form>
</app-modal>

<!-- Delete modal -->
<app-confirm-modal
  [open]="deleteOpen()"
  [busy]="deleting()"
  [error]="deleteError()"
  title="Supprimer le scope ?"
  confirmLabel="Supprimer définitivement"
  busyLabel="Suppression…"
  danger
  (confirm)="confirmDelete()"
  (close)="closeDelete()"
>
  <span class="font-mono font-medium text-gray-700 dark:text-gray-300">{{ toDelete()?.name }}</span> sera supprimé. Les permissions qui le référencent peuvent devenir invalides.
</app-confirm-modal>
